import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo } from 'react';
import { ChevronLeft, Heart, IconFormIcons, Target } from 'shared/assets/icons';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import { Task } from 'shared/types/task';
import { TaskActionLifeArea, TaskActionType } from 'shared/types/task-action';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import * as Styled from './action-timeslot-title.style';

export type ActionTimeslotTitleProps = {
  task: Task;
};

const GoalTimeslotTitle: React.FC<ActionTimeslotTitleProps> = ({ task }) => {
  const lifeAreas = useLocalizedLifeAreas();
  const goals = useGoals();

  const goal = useMemo(
    () => goals.find(({ id }) => id === task.action?.meta.id),
    [goals, task.action?.meta.id],
  );

  const completionPercentage = useMemo(
    () =>
      goal
        ? getCompletionPercentage(getGoalCompletion(goal, { goals }))
        : undefined,
    [goal, goals],
  );

  const color = useMemo(
    () =>
      goal
        ? getLinkedItemColor(goal.id, { goals, lifeAreas }) ?? undefined
        : undefined,
    [goal, goals, lifeAreas],
  );

  if (!goal) {
    return (
      <Styled.Container>
        <Styled.IconContainer $color={color}>
          <Icon icon={Target} />
        </Styled.IconContainer>
        <Styled.Content>
          <Styled.Title>{task.name}</Styled.Title>
        </Styled.Content>
      </Styled.Container>
    );
  }

  return (
    <Styled.Container>
      <Styled.IconContainer $color={color}>
        <CircleProgress
          percentage={completionPercentage}
          hidePercentage
          isSmall
        />
      </Styled.IconContainer>
      <Styled.Content>
        <Styled.Title>{goal.name}</Styled.Title>
        <Styled.ChevronContainer>
          <Icon icon={ChevronLeft} />
        </Styled.ChevronContainer>
      </Styled.Content>
    </Styled.Container>
  );
};

const LifeAreaTimeslotTitle: React.FC<ActionTimeslotTitleProps> = ({
  task,
}) => {
  const lifeAreas = useLocalizedLifeAreas();

  const lifeArea = useMemo(
    () =>
      lifeAreas.find(
        ({ id }) => id === (task.action as TaskActionLifeArea)?.meta?.id,
      ),
    [lifeAreas, task.action],
  );

  if (!lifeArea) {
    return (
      <Styled.Container>
        <Styled.IconContainer>
          <Icon icon={Heart} />
        </Styled.IconContainer>
        <Styled.Content>
          <Styled.Title>{task.name}</Styled.Title>
        </Styled.Content>
      </Styled.Container>
    );
  }

  return (
    <Styled.Container>
      <Styled.IconContainer $color={lifeArea.color ?? undefined}>
        <Icon icon={IconFormIcons[lifeArea.iconName] ?? Heart} />
      </Styled.IconContainer>
      <Styled.Content>
        <Styled.Title>{lifeArea.name}</Styled.Title>
        <Styled.ChevronContainer>
          <Icon icon={ChevronLeft} />
        </Styled.ChevronContainer>
      </Styled.Content>
    </Styled.Container>
  );
};

export const ActionTimeslotTitle: React.FC<ActionTimeslotTitleProps> = ({
  task,
}) => {
  if (task.action?.type === TaskActionType.GoalTimeslot) {
    return <GoalTimeslotTitle task={task} />;
  }

  return <LifeAreaTimeslotTitle task={task} />;
};
